import { createSlice } from '@reduxjs/toolkit';
import {
  fetchMonetizationConfigThunk,
  onboardConnectThunk,
  fetchConnectStatusThunk,
  fetchGroupPricingThunk,
  setGroupPricingThunk,
  subscribeToGroupThunk,
  fetchMySubscriptionThunk,
  fetchMySubscriptionsThunk,
  cancelSubscriptionThunk,
  fetchEarningsThunk,
  fetchCreatorDashboardThunk,
} from './monetizationActionThunks';

const initialState = {
  config: { monetization_live: false, stripe_publishable_key: null }, // safe default: sandbox-only until config loads
  configLoaded: false,
  connectStatus: { onboarding_complete: false, payouts_enabled: false },
  pricingByGroup: {},        // { [groupId]: { price_cents, currency, billing_type, active } }
  subscriptionByGroup: {},   // { [groupId]: { id, status, current_period_end } | null }
  mySubscriptions: [],       // [{ id, group_id, group_name, status, current_period_end }]
  earnings: null,
  creatorDashboard: null,
  creatorDashboardLoading: false,
  creatorDashboardError: null,
  loading: false,
  error: null,
};

const monetizationSlice = createSlice({
  name: 'monetization',
  initialState,
  reducers: {
    clearMonetizationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonetizationConfigThunk.fulfilled, (state, { payload }) => {
        state.config = payload;
        state.configLoaded = true;
      })
      .addCase(fetchMonetizationConfigThunk.rejected, (state) => {
        // Fail safe: keep the sandbox-only default rather than assuming live.
        state.configLoaded = true;
      });

    builder
      .addCase(onboardConnectThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(onboardConnectThunk.fulfilled, (state) => { state.loading = false; })
      .addCase(onboardConnectThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    builder
      .addCase(fetchConnectStatusThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchConnectStatusThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.connectStatus = payload;
      })
      .addCase(fetchConnectStatusThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    builder
      .addCase(fetchGroupPricingThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchGroupPricingThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.pricingByGroup[payload.groupId] = payload.data;
      })
      .addCase(fetchGroupPricingThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; })
      .addCase(setGroupPricingThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(setGroupPricingThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.pricingByGroup[payload.groupId] = payload.data;
      })
      .addCase(setGroupPricingThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    // subscribeToGroupThunk deliberately has no `.fulfilled` case -- this is
    // not the same bug shape as the other thunks here. It's a Stripe Checkout
    // redirect: on success it calls `window.location.assign(checkout_url)`
    // and navigates the whole browser to Stripe's hosted page before a real
    // subscription exists. There is no subscription data in its payload
    // (just `{checkout_url}`) and no meaningful state to write -- the actual
    // subscription only becomes real after the user returns from Stripe,
    // which is what fetchMySubscriptionThunk/fetchMySubscriptionsThunk (both
    // fixed below) are for. `.pending`/`.rejected` (already present) correctly
    // cover the one real failure mode of this thunk: the checkout-url fetch
    // itself failing before ever reaching Stripe.
    builder
      .addCase(subscribeToGroupThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(subscribeToGroupThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    builder
      .addCase(fetchMySubscriptionThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMySubscriptionThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.subscriptionByGroup[payload.groupId] = payload.data;
      })
      .addCase(fetchMySubscriptionThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    builder
      .addCase(fetchMySubscriptionsThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMySubscriptionsThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.mySubscriptions = payload;
      })
      .addCase(fetchMySubscriptionsThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    builder
      .addCase(cancelSubscriptionThunk.fulfilled, (state, { payload }) => {
        const existing = state.subscriptionByGroup[payload.groupId];
        if (existing) existing.status = 'canceled';
        const listItem = state.mySubscriptions.find((s) => s.group_id === payload.groupId);
        if (listItem) listItem.status = 'canceled';
      })
      .addCase(cancelSubscriptionThunk.rejected, (state, { payload }) => { state.error = payload; });

    builder
      .addCase(fetchEarningsThunk.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchEarningsThunk.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.earnings = payload;
      })
      .addCase(fetchEarningsThunk.rejected, (state, { payload }) => { state.loading = false; state.error = payload; });

    builder
      .addCase(fetchCreatorDashboardThunk.pending, (state) => {
        state.creatorDashboardLoading = true;
        state.creatorDashboardError = null;
      })
      .addCase(fetchCreatorDashboardThunk.fulfilled, (state, { payload }) => {
        state.creatorDashboardLoading = false;
        state.creatorDashboard = payload;
      })
      .addCase(fetchCreatorDashboardThunk.rejected, (state, { payload }) => {
        state.creatorDashboardLoading = false;
        state.creatorDashboardError = payload;
      });
  },
});

export const { clearMonetizationError } = monetizationSlice.actions;
export default monetizationSlice.reducer;

// --- Selectors ---
export const selectMonetizationConfig = (state) => state.monetization.config;
export const selectMonetizationConfigLoaded = (state) => state.monetization.configLoaded;
export const selectConnectStatus = (state) => state.monetization.connectStatus;
export const selectGroupPricing = (state, groupId) => state.monetization.pricingByGroup[groupId] || null;
export const selectMySubscription = (state, groupId) => state.monetization.subscriptionByGroup[groupId];
export const selectMySubscriptions = (state) => state.monetization.mySubscriptions;
export const selectEarnings = (state) => state.monetization.earnings;
export const selectCreatorDashboard = (state) => state.monetization.creatorDashboard;
export const selectCreatorDashboardLoading = (state) => state.monetization.creatorDashboardLoading;
export const selectCreatorDashboardError = (state) => state.monetization.creatorDashboardError;
export const selectMonetizationLoading = (state) => state.monetization.loading;
export const selectMonetizationError = (state) => state.monetization.error;
